import { useEffect, useState } from 'react';
import {
  deliveryFileUrl,
  deliveryVideoStreamUrl,
  getDeliveryPortal,
  type DeliveryPortal,
} from './api';
import { ProjectArchivePanel } from './ProjectArchivePanel';
import { RetentionPanel } from './RetentionPanel';

function categoryLabel(category: string): string {
  const labels: Record<string, string> = {
    ELECTRONIC: '電子納品用',
    INTERNAL: '社内保管用',
    PAPER: '紙納品用',
    SCREEN: '安全教育PDF',
    VIDEO: '安全教育動画',
  };
  return labels[category] ?? category;
}

function sizeLabel(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.ceil(bytes / 1024)} KB`;
}

export function DeliveryPortalPanel({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (error: unknown) => void;
}) {
  const [portal, setPortal] = useState<DeliveryPortal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void getDeliveryPortal(companyId)
      .then((value) => {
        if (active) setPortal(value);
      })
      .catch(onError)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId]);

  if (loading) return <div className="card empty-state">納品済み教材を確認しています…</div>;
  if (portal === null || portal.projects.length === 0) {
    return <div className="card empty-state">現在公開中の教材はありません。</div>;
  }

  return (
    <section className="delivery-portal">
      <div className="delivery-hero">
        <p className="eyebrow">SECURE DELIVERY</p>
        <h2>{portal.company.name} 専用納品ページ</h2>
        <p>
          公開済みの安全訓練教材だけを表示しています。One
          Color内部のGoogleアカウント情報は表示されません。
        </p>
      </div>
      {portal.projects.map((project) => (
        <section className="delivery-project" key={project.id}>
          <div className="section-heading">
            <div>
              <p className="eyebrow">PROJECT</p>
              <h3>{project.name}</h3>
            </div>
          </div>
          <RetentionPanel
            canRetry={false}
            companyId={portal.company.id}
            onError={onError}
            projectId={project.id}
          />
          <ProjectArchivePanel
            canGenerate={false}
            companyId={portal.company.id}
            onError={onError}
            projectId={project.id}
          />
          {project.months.map((month) => {
            const video = month.files.find((item) => item.category === 'VIDEO');
            const documents = month.files.filter((item) => item.category !== 'VIDEO');
            return (
              <article className="delivery-month" key={month.materialId}>
                <header>
                  <span className="delivery-month-badge">
                    {month.targetMonth.replace('-', '年')}月
                  </span>
                  <div>
                    <h4>月例安全教育</h4>
                    <p>{month.displayId}</p>
                  </div>
                </header>
                {video !== undefined && (
                  <div className="customer-video-frame">
                    <video
                      controls
                      controlsList="nodownload"
                      disablePictureInPicture
                      preload="metadata"
                      src={deliveryVideoStreamUrl(portal.company.id, project.id, video.id)}
                    >
                      このブラウザは動画再生に対応していません。
                    </video>
                    <p className="muted">
                      再生・一時停止・シーク・音量・全画面に対応しています。動画downloadは初期設定で非表示です。
                    </p>
                  </div>
                )}
                <div className="delivery-file-grid">
                  {documents.map((file) => (
                    <a
                      className="delivery-file-card"
                      href={deliveryFileUrl(portal.company.id, project.id, file.id)}
                      key={file.id}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <span>{categoryLabel(file.category)}</span>
                      <strong>{file.displayName}</strong>
                      <small>{sizeLabel(file.sizeBytes)}</small>
                    </a>
                  ))}
                </div>
              </article>
            );
          })}
        </section>
      ))}
    </section>
  );
}
